import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import notificationService from '../services/notificationService';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../UserContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const { userData, token } = useContext(UserContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    soundEnabled: localStorage.getItem('notificationSound') !== 'false',
    desktopEnabled: localStorage.getItem('desktopNotifications') !== 'false',
    autoRefresh: localStorage.getItem('notificationAutoRefresh') !== 'false',
    refreshInterval: parseInt(localStorage.getItem('notificationRefreshInterval') || '60000')
  });

  // Initialize notification service
  useEffect(() => {
    let unsubscribe = null;

    console.log('[NotificationContext] useEffect triggered:', {
      hasUserData: !!userData,
      userId: userData?.User?.id,
      farmId: userData?.Farm?.id,
      hasToken: !!token,
      tokenLength: token?.length
    });

    const initializeNotifications = async () => {
      try {
        // Check if userData is available
        if (userData?.User?.id && userData?.Farm?.id) {
          console.log('[NotificationContext] User data available, initializing...');
          const initialized = await notificationService.initialize();
          setIsInitialized(initialized);

          // Subscribe to incoming messages
          if (initialized) {
            console.log('[NotificationContext] FCM initialized, subscribing to messages');
            unsubscribe = notificationService.subscribe((payload) => {
              console.log('[NotificationContext] New notification received:', payload);
              // Refresh notifications when new message arrives
              fetchNotifications();
            });

            // Fetch initial notifications after successful initialization
            console.log('[NotificationContext] Fetching initial notifications...');
            fetchNotifications();
          }
        } else {
          console.log('[NotificationContext] Waiting for user data to be loaded...', {
            hasUser: !!userData?.User,
            hasFarm: !!userData?.Farm
          });
          // Don't set initialized to true yet, keep waiting for userData
        }
      } catch (error) {
        console.error('[NotificationContext] Error initializing notifications:', error);
        setError(error.message);
        // Set initialized to true even on error to hide loading spinner
        setIsInitialized(true);
      }
    };

    // Initialize when userData becomes available
    if (userData?.User?.id && userData?.Farm?.id) {
      initializeNotifications();
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [userData, token]);

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async (params = {}) => {
    try {
      const farmId = userData?.Farm?.id;

      console.log('[NotificationContext] fetchNotifications called:', {
        farmId,
        hasToken: !!token,
        tokenLength: token?.length,
        tokenPreview: token ? token.substring(0, 30) + '...' : 'none',
        params
      });

      if (!farmId) {
        console.log('[NotificationContext] Farm ID not available yet, skipping notification fetch');
        return [];
      }

      if (!token) {
        console.log('[NotificationContext] Auth token not available yet, skipping notification fetch');
        return [];
      }

      setLoading(true);
      setError(null);

      console.log('[NotificationContext] Calling notificationService.fetchNotifications...');
      const response = await notificationService.fetchNotifications(farmId, token, params);

      // Backend returns: { status: 200, message: "...", items: [...], totalCount: N }
      console.log('[NotificationContext] Response received:', {
        status: response?.status,
        hasItems: !!response?.items,
        itemsCount: response?.items?.length
      });

      if (response.status === 200 && response.items) {
        const notificationList = response.items.map(item => ({
          id: item.Alert?.id,
          type: item.Alert?.type,
          status: item.Alert?.status,
          severity: item.Alert?.severity,
          title: i18n.language === 'ar' ? item.Alert?.titleAr : item.Alert?.title,
          description: i18n.language === 'ar' ? item.Alert?.descriptionAr : item.Alert?.description,
          reportedDate: item.Alert?.reportedDate,
          resolvedDate: item.Alert?.resolvedDate,
          // Handle Optional objects - check if they have a value
          employee: item.Employee?.present ? item.Employee.get : null,
          span: item.Span?.present ? item.Span.get : null,
          raw: item.Alert
        }));

        console.log('[NotificationContext] Processed notifications:', {
          count: notificationList.length,
          notifications: notificationList
        });

        setNotifications(notificationList);

        // Calculate unread count (active notifications)
        const unread = notificationList.filter(n => n.status === 'active').length;
        setUnreadCount(unread);

        console.log('[NotificationContext] State updated:', {
          totalNotifications: notificationList.length,
          unreadCount: unread
        });

        // Update badge in document title
        document.title = unread > 0 ? `(${unread}) EgroCare Pro` : 'EgroCare Pro';

        return notificationList;
      }
    } catch (error) {
      console.error('[NotificationContext] Error fetching notifications:', {
        message: error.message,
        stack: error.stack
      });
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [userData, token, i18n.language]);

  // Auto-refresh notifications
  useEffect(() => {
    if (notificationSettings.autoRefresh && isInitialized) {
      const interval = setInterval(() => {
        fetchNotifications();
      }, notificationSettings.refreshInterval);

      return () => clearInterval(interval);
    }
  }, [notificationSettings.autoRefresh, notificationSettings.refreshInterval, fetchNotifications, isInitialized]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      if (!token) {
        console.log('Auth token not available');
        return false;
      }

      await notificationService.updateNotificationStatus(notificationId, token, 'dismissed');

      // Update local state
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, status: 'dismissed' } : n
      ));

      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));

      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      setError(error.message);
      return false;
    }
  }, [token]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const farmId = userData?.Farm?.id;

      if (!farmId) {
        console.log('Farm ID not available, cannot mark all as read');
        return false;
      }

      if (!token) {
        console.log('Auth token not available');
        return false;
      }

      const response = await notificationService.markAllAsRead(farmId, token);

      if (response.status === 200) {
        // Update local state
        setNotifications(prev => prev.map(n => ({ ...n, status: 'dismissed' })));
        setUnreadCount(0);

        // Update document title
        document.title = 'EgroCare Pro';

        return true;
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
      setError(error.message);
      return false;
    }
  }, [userData, token]);

  // Delete notification (mark as resolved)
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      if (!token) {
        console.log('Auth token not available');
        return false;
      }

      await notificationService.updateNotificationStatus(notificationId, token, 'resolved');

      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      // Update unread count if it was active
      const notification = notifications.find(n => n.id === notificationId);
      if (notification?.status === 'active') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      return true;
    } catch (error) {
      console.error('Error deleting notification:', error);
      setError(error.message);
      return false;
    }
  }, [token, notifications]);

  // Resolve notification with details
  const resolveNotification = useCallback(async (notificationId, resolutionDetails) => {
    try {
      if (!token) {
        console.log('Auth token not available');
        return false;
      }

      await notificationService.updateNotificationStatus(notificationId, token, 'resolved', resolutionDetails);

      // Update local state
      setNotifications(prev => prev.map(n =>
        n.id === notificationId
          ? { ...n, status: 'resolved', resolutionDetails, resolutionStatus: 'resolved', resolvedDate: Date.now() }
          : n
      ));

      // Update unread count if it was active
      const notification = notifications.find(n => n.id === notificationId);
      if (notification?.status === 'active') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      return true;
    } catch (error) {
      console.error('Error resolving notification:', error);
      setError(error.message);
      return false;
    }
  }, [token, notifications]);

  // Update notification settings
  const updateSettings = useCallback((newSettings) => {
    const updatedSettings = { ...notificationSettings, ...newSettings };
    setNotificationSettings(updatedSettings);

    // Persist to localStorage
    Object.entries(newSettings).forEach(([key, value]) => {
      switch(key) {
        case 'soundEnabled':
          localStorage.setItem('notificationSound', value.toString());
          break;
        case 'desktopEnabled':
          localStorage.setItem('desktopNotifications', value.toString());
          if (value) {
            notificationService.requestPermission();
          }
          break;
        case 'autoRefresh':
          localStorage.setItem('notificationAutoRefresh', value.toString());
          break;
        case 'refreshInterval':
          localStorage.setItem('notificationRefreshInterval', value.toString());
          break;
      }
    });
  }, [notificationSettings]);

  // Send custom notification (for admins)
  const sendNotification = useCallback(async (tenantId, title, description, data) => {
    try {
      if (!token) {
        console.log('Auth token not available');
        return false;
      }

      const response = await notificationService.sendCustomNotification(
        tenantId,
        token,
        title,
        description,
        data
      );

      if (response.status === 200) {
        // Refresh notifications after sending
        fetchNotifications();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error sending notification:', error);
      setError(error.message);
      return false;
    }
  }, [token, fetchNotifications]);

  // Get notification type color
  const getTypeColor = (type) => {
    switch(type) {
      case 'tasks': return '#007bff';
      case 'problems': return '#dc3545';
      case 'inventory': return '#ffc107';
      case 'weather': return '#17a2b8';
      case 'general': return '#6c757d';
      default: return '#28a745';
    }
  };

  // Get notification type icon
  const getTypeIcon = (type) => {
    switch(type) {
      case 'tasks': return '📋';
      case 'problems': return '⚠️';
      case 'inventory': return '📦';
      case 'weather': return '🌤️';
      case 'general': return '📢';
      default: return '🔔';
    }
  };

  // Format notification date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return i18n.language === 'ar' ? 'الآن' : 'Just now';
    if (diffMins < 60) return i18n.language === 'ar' ? `منذ ${diffMins} دقيقة` : `${diffMins} mins ago`;
    if (diffHours < 24) return i18n.language === 'ar' ? `منذ ${diffHours} ساعة` : `${diffHours} hours ago`;
    if (diffDays < 7) return i18n.language === 'ar' ? `منذ ${diffDays} يوم` : `${diffDays} days ago`;

    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US');
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    isInitialized,
    notificationSettings,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    resolveNotification,
    updateSettings,
    sendNotification,
    getTypeColor,
    getTypeIcon,
    formatDate
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;